import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type JSX } from "react";

import { useCan } from "../../../auth/permissions.js";
import { toast } from "../../../components/ui/use-toast.js";
import { ApiError } from "../../../lib/api.js";
import { marketingApi } from "../client.js";
import { SectionCardHeader } from "../shared.js";
import type { Draft, Platform } from "../types.js";
import { DraftReviewDialog } from "../workbench/DraftReviewDialog.js";
import { AngleSelect, PlatformSelect } from "./studioShared.js";

/**
 * Create New — the operator writes what they need and the agent generates a
 * grounded, on-brand draft (with its visual). The result drops into the
 * review queue and opens straight into the shared `DraftReviewDialog` so it
 * can be accepted / edited / re-generated like any other draft.
 *
 * `marketing:edit` gated — read-only viewers get an explainer instead of the
 * form (mirrors the header controls + Settings panels).
 */
export function CreatePage(): JSX.Element {
  const canEdit = useCan("marketing:edit");
  const qc = useQueryClient();
  const [platform, setPlatform] = useState<Platform>("linkedin_post");
  const [angle, setAngle] = useState("");
  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [lastDraft, setLastDraft] = useState<Draft | null>(null);
  const [open, setOpen] = useState(false);

  const compose = useMutation({
    mutationFn: () =>
      marketingApi.compose({
        brief: brief.trim(),
        platform,
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(angle ? { angle } : {}),
      }),
    onSuccess: (draft: Draft) => {
      setLastDraft(draft);
      setOpen(true);
      qc.invalidateQueries({ queryKey: ["marketing", "queue"] });
      qc.invalidateQueries({ queryKey: ["marketing", "economics"] });
      toast({
        title: "Draft created",
        description: "It's in the approve queue — review, edit, or approve it there.",
      });
    },
    onError: (err) => {
      toast({
        variant: "destructive",
        title: "Could not generate",
        description:
          err instanceof ApiError ? err.message : "The marketing agent failed to draft this.",
      });
    },
  });

  if (!canEdit) {
    return (
      <Card>
        <SectionCardHeader title="Create new content" />
        <CardContent sx={{ px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            You have read-only access to Marketing. Ask an admin for{" "}
            <Box component="code" sx={{ fontFamily: "ui-monospace, monospace" }}>
              marketing:edit
            </Box>{" "}
            to generate content.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const canSubmit = brief.trim().length > 0 && !compose.isPending;

  return (
    <Box sx={{ display: "grid", gap: 2 }} data-testid="marketing-create">
      <Card>
        <SectionCardHeader
          title="Create new content"
          subtitle="Tell the agent what you need. It drafts grounded, on-brand copy with a visual, then you accept or edit it."
        />
        <CardContent sx={{ px: 2.5, py: 2, display: "grid", gap: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            <PlatformSelect value={platform} onChange={setPlatform} disabled={compose.isPending} />
            <AngleSelect value={angle} onChange={setAngle} disabled={compose.isPending} />
          </Box>
          <Box component="label" sx={{ display: "grid", gap: 0.75 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
              Headline / title{" "}
              <Box component="span" sx={{ color: "text.disabled", fontWeight: 400 }}>
                (optional)
              </Box>
            </Typography>
            <Box
              component="input"
              data-testid="create-title"
              value={title}
              disabled={compose.isPending}
              onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
              placeholder="Leave blank to let the agent write one"
              maxLength={300}
              sx={{
                font: "inherit",
                fontSize: 14,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
                color: "text.primary",
                "&:focus": { outline: (t) => `2px solid ${t.palette.primary.main}` },
              }}
            />
          </Box>
          <Box component="label" sx={{ display: "grid", gap: 0.75 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
              What should this be about?
            </Typography>
            <Box
              component="textarea"
              data-testid="create-brief"
              value={brief}
              disabled={compose.isPending}
              onChange={(e) => setBrief((e.target as HTMLTextAreaElement).value)}
              placeholder="e.g. A post about how our SD-WAN failover keeps branch phones up during an internet outage — aimed at IT directors."
              maxLength={4000}
              rows={5}
              sx={{
                font: "inherit",
                fontSize: 14,
                resize: "vertical",
                px: 1.5,
                py: 1,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
                color: "text.primary",
                "&:focus": { outline: (t) => `2px solid ${t.palette.primary.main}` },
              }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              type="button"
              variant="contained"
              data-testid="create-generate"
              disabled={!canSubmit}
              onClick={() => compose.mutate()}
            >
              {compose.isPending ? "Generating…" : "Generate"}
            </Button>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Grounded in the knowledge base · never mentions competitors.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* No persistent "latest draft" card — the generated draft lands in the
          ONE approve queue (Pending approval). We pop the review dialog once,
          right after generating, so the operator can immediately review the new
          draft + its image; closing it leaves the draft in the queue. */}
      <DraftReviewDialog draft={lastDraft} open={open} onClose={() => setOpen(false)} />
    </Box>
  );
}
